import { useContext, useEffect, useState } from 'react';
import { subject } from '@casl/ability';
import { useAbility } from '@casl/react';
import { CircularProgress } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { Box } from '@mui/system';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from "@tanstack/react-query";

import DataLoadingError from "@components/common/DataLoadingError";
import useSessionDetailsQuery from "@components/common/hooks/api/useSessionDetailsQuery";
import { API_URLS, axiosRequest } from "@lib/axios";
import { PageContext } from '@lib/contexts/pageContext';
import moment from '@lib/moment';
import { AbilityContext, Subjects } from '@lib/permissions';
import { scrollToAlert, sortDisplayOrder, } from '@lib/utils';
import SessionForm from '../SessionForm';

const EditSession = () => {
  const [submitting, setSubmitting] = useState(false);
  const valuesSetState = useState(null);

  const { setBreadcrumbValue, setPageMessage, setPageErrorMessage } = useContext(PageContext);

  const ability = useAbility(AbilityContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { sessionID } = useParams();

  const form = useForm({
    mode: 'onSubmit',
    defaultValues: {
      session_name: null,
      lesson: null,
      start: null,
      end: null,
      delivery_method: [],
      delivery_url: null,
      delivery_location: null,
      other_details: null,
      recipes: null,
      delivery_language: [],
      material_language: [],
      demonstration: null,
    }
  });

  const { data: session, isLoading, isError, error} = useSessionDetailsQuery(sessionID);
  const event = session?.event;

  useEffect(() => {
    if (session) {
      setBreadcrumbValue(':event', event);
      setBreadcrumbValue(':session', session);

      if (event.event_type === 'CLASS' && event.curriculum?.lessons) {
        sortDisplayOrder(event.curriculum.lessons, 'lesson_name');
      }
    }
    // eslint-disable-next-line
  }, [event, session]);

  const updateMutation = useMutation({
    mutationFn: async (input) => axiosRequest({
      method: 'PUT',
      url: `${API_URLS.SESSIONS}/${session.id}`,
      data: input,
    }),
    onSuccess: async (session) => {
      await queryClient.invalidateQueries({ queryKey: ['events'] });
      await queryClient.invalidateQueries({ queryKey: ['sessions'] });
      navigate(`/events/${event.id}/${session.id}`, { state: { message: 'Session updated.' }});
    },
    onError: async (err) => {
      setPageErrorMessage('Error updating session. Please try again.', err);
      console.log('[EditSession] error:', err);
      scrollToAlert();
    },
    onSettled: () => {
      setSubmitting(false);
    },
  });

  /**
   * Submit handler.
   *
   * @param values
   */
  const onSubmit = async (values) => {
    setPageMessage(null);
    setPageErrorMessage(null);
    setSubmitting(true);

    const input = Object.keys(values).reduce((acc, key) => {
      const value = values[key];
      switch (key) {
        case 'lesson':
          if (value?.id) {
            acc[`${key}_id`] = value?.id;
          }
          if (event.event_type === 'BOOTH') {
            acc.session_name = value?.label;
          }
          break;
        case 'start':
        case 'end':
          acc[key] = moment(value).toISOString(true);
          break;
        case 'session_name':
          acc[key] = value?.trim();
          break;
        case 'delivery_method':
        case 'delivery_url':
        case 'delivery_location':
        case 'other_details':
        case 'recipes':
          acc[key] = value?.id || value;
          break;
        case 'delivery_language':
        case 'material_language':
          acc[key] = (value || []);
          break;
        case 'collaborators':
          acc[key] = value ? value.map(v => v.id) : [];
          break;
        case 'demonstration':
          acc[key] = value === 'true';
          break;
        default: break;
      }
      return acc;
    }, { });

    updateMutation.mutate(input);
  };

  if (isLoading) {
    return (
      <Box width="100%" textAlign="center">
        <CircularProgress />
      </Box>
    );
  }

  if (isError && error.status !== 404) {
    return <DataLoadingError title={'Failed to load session!'} errors={error.errors} />;
  }

  if (!session || !event) {
    return <>You do not have access, or the session was not found.</>;
  }

  if (ability.cannot('edit', subject(Subjects.SESSION, session))) {
    return <>You do not have permission to edit this session.</>;
  }

  return (
    <Grid container gap={3} wrap={'nowrap'} columns={10}>
      <Grid xs={10}>
        <SessionForm
          form={form}
          session={session}
          event={event}
          submitting={submitting}
          onSubmit={onSubmit}
          valuesSetState={valuesSetState}
        />
      </Grid>
    </Grid>
  );
};

export default EditSession;
